/**
 * Основной макет приложения АСУБП.
 * Ролевая навигация: меню определяется по службам, правам и роли пользователя.
 * Включает боковую навигацию (MUI Drawer), шапку (AppBar) и область контента.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Plane,
  LayoutDashboard,
  Shield,
  BarChart3,
  Users,
  Building2,
  MapPin,
  FileText,
  Bell,
  Settings,
  LogOut,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Radar,
  ChevronDown,
  ChevronUp,
  Lock,
  ClipboardList,
  ScrollText,
  HelpCircle,
  KeyRound,
  RefreshCw,
  Menu as MenuIcon,
  BarChart2,
  Activity,
  AlertTriangle,
  BookMarked,
  CalendarDays,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import CommandPalette from '@/components/CommandPalette';
import GlobalSearch from '@/components/GlobalSearch';
import AIAssistant from '@/components/AIAssistant';
import { PageTransition } from '@/components/PageTransition';
import { useOnboardingTour } from '@/hooks/useOnboardingTour';
import { getUnreadCount, getFlights, getSafetyEvents, getServices, getSystemSettings } from '@/api/endpoints';
import { getBrand } from '@/theme/brands';
import type { User } from '@/types';

const DRAWER_WIDTH = 260;
const DRAWER_COLLAPSED_WIDTH = 72;

// ============================================================
// Типы навигации
// ============================================================

interface NavItem {
  key: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  children?: NavItem[];
  divider?: boolean;
  badge?: number;
  tour?: string;
}

// ============================================================
// Определение роли пользователя
// ============================================================

type UserNavRole = 'admin' | 'grpi' | 'service' | 'pilot' | 'viewer';

function getUserNavRole(user: User | null): UserNavRole {
  if (!user) return 'viewer';

  // Admin/manager: has_full_access or has admin role
  if (user.has_full_access) return 'admin';
  const roleNames = (user.roles ?? []).map((r) => (r?.name ?? '').toLowerCase());
  if (roleNames.includes('admin') || roleNames.includes('safety_manager') || roleNames.includes('flight_director')) {
    return 'admin';
  }

  // GRPI: user belongs to a flight data service
  const hasFlightDataService = user.services?.some((s) => s.is_flight_data_service);
  if (hasFlightDataService) return 'grpi';

  // Service user: user belongs to non-flight-data service
  if (user.services && user.services.length > 0) return 'service';

  // Pilot: has pilot role
  if (roleNames.includes('pilot')) return 'pilot';

  return 'viewer';
}

function getUserServiceName(user: User | null): string | null {
  if (!user || !user.services || user.services.length === 0) return null;
  // Return the first service code (e.g., "GRPI", "IAS", "SBP")
  return user.services[0].code;
}

// ============================================================
// Navigation configs per role
// ============================================================

function getAdminNav(
  services?: Array<{ code: string; name: string; description?: string }>,
): NavItem[] {
  // Динамические пункты служб
  const serviceChildren: NavItem[] = [
    { key: '/safety/events', icon: <AlertTriangle size={16} />, label: 'Все события', tour: 'safety-events' },
    ...(services || []).map((s) => ({
      key: `/service/${s.code}`,
      icon: <Shield size={16} />,
      label: s.name,
      description: s.description || undefined,
    })),
  ];

  return [
    { key: '/dashboard', icon: <LayoutDashboard size={20} />, label: 'Центр управления', tour: 'dashboard' },
    {
      key: 'decoding-group',
      icon: <ClipboardList size={20} />,
      label: 'Полётные данные',
      children: [
        { key: '/flights', icon: <Plane size={16} />, label: 'Все полёты', tour: 'flights' },
        { key: '/accounting-journal', icon: <CalendarDays size={16} />, label: 'Журнал смены' },
        { key: '/reginfo-analysis', icon: <BarChart2 size={16} />, label: 'Анализ параметров' },
        { key: '/safety/catalog', icon: <BookMarked size={16} />, label: 'Каталог событий' },
      ],
    },
    {
      key: 'safety-group',
      icon: <Shield size={20} />,
      label: 'События служб',
      children: serviceChildren,
    },
    { key: '/analytics', icon: <BarChart3 size={20} />, label: 'Аналитика' },
    { key: '/reports', icon: <FileText size={20} />, label: 'Отчёты' },
    { key: '_divider1', label: '', divider: true },
    {
      key: 'references-group',
      icon: <BookOpen size={20} />,
      label: 'Справочники',
      children: [
        { key: '/airports', icon: <MapPin size={16} />, label: 'Аэропорты' },
        { key: '/fleet', icon: <Plane size={16} />, label: 'Воздушные суда' },
        { key: '/pilots', icon: <Radar size={16} />, label: 'Лётный состав' },
        { key: '/organization', icon: <Building2 size={16} />, label: 'Эскадрильи' },
      ],
    },
    {
      key: 'admin-group',
      icon: <Settings size={20} />,
      label: 'Управление',
      children: [
        { key: '/admin/users', icon: <Users size={16} />, label: 'Пользователи', tour: 'users' },
        { key: '/admin/roles', icon: <Lock size={16} />, label: 'Роли и права' },
        { key: '/admin/services', icon: <Building2 size={16} />, label: 'Службы' },
        { key: '/admin/settings', icon: <Settings size={16} />, label: 'Настройки' },
        { key: '/system-status', icon: <Activity size={16} />, label: 'Состояние системы' },
        { key: '/audit-log', icon: <ScrollText size={16} />, label: 'Журнал аудита' },
        { key: '/auth-journal', icon: <KeyRound size={16} />, label: 'Журнал авторизаций' },
        { key: '/sync', icon: <RefreshCw size={16} />, label: 'Синхронизация MDB', tour: 'sync' },
      ],
    },
  ];
}

function getGrpiNav(pendingCount?: number): NavItem[] {
  return [
    { key: '/dashboard', icon: <LayoutDashboard size={20} />, label: 'Центр управления', tour: 'dashboard' },
    {
      key: 'decoding-group',
      icon: <ClipboardList size={20} />,
      label: 'Полётные данные',
      children: [
        { key: '/flights', icon: <Plane size={16} />, label: 'Все полёты', badge: pendingCount, tour: 'flights' },
        { key: '/accounting-journal', icon: <CalendarDays size={16} />, label: 'Журнал смены', tour: 'accounting-journal' },
        { key: '/reginfo-analysis', icon: <BarChart2 size={16} />, label: 'Анализ параметров' },
        { key: '/safety/catalog', icon: <BookMarked size={16} />, label: 'Каталог событий' },
      ],
    },
    { key: '/analytics', icon: <BarChart3 size={20} />, label: 'Аналитика' },
    { key: '/reports', icon: <FileText size={20} />, label: 'Отчёты' },
    { key: '_divider1', label: '', divider: true },
    {
      key: 'references-group',
      icon: <BookOpen size={20} />,
      label: 'Справочники',
      children: [
        { key: '/airports', icon: <MapPin size={16} />, label: 'Аэропорты' },
        { key: '/fleet', icon: <Plane size={16} />, label: 'Воздушные суда' },
        { key: '/pilots', icon: <Radar size={16} />, label: 'Лётный состав' },
        { key: '/organization', icon: <Building2 size={16} />, label: 'Эскадрильи' },
      ],
    },
  ];
}

function getServiceNav(
  openCount?: number,
  services?: Array<{ code: string; name: string }>,
): NavItem[] {
  const serviceItems: NavItem[] = (services || []).map((s) => ({
    key: `/service/${s.code}`,
    icon: <Shield size={20} />,
    label: s.name,
    badge: openCount,
    tour: 'safety-events',
  }));

  return [
    ...(serviceItems.length > 0
      ? serviceItems
      : [{ key: '/safety/events', icon: <ClipboardList size={20} />, label: 'Все события', badge: openCount }]),
    { key: '/analytics', icon: <BarChart3 size={20} />, label: 'Аналитика' },
    { key: '/reports', icon: <FileText size={20} />, label: 'Отчёты' },
    { key: '_divider1', label: '', divider: true },
    {
      key: 'references-group',
      icon: <BookOpen size={20} />,
      label: 'Справочники',
      children: [
        { key: '/airports', icon: <MapPin size={16} />, label: 'Аэропорты' },
        { key: '/fleet', icon: <Plane size={16} />, label: 'Воздушные суда' },
        { key: '/pilots', icon: <Radar size={16} />, label: 'Лётный состав' },
      ],
    },
  ];
}

function getPilotNav(): NavItem[] {
  return [
    { key: '/flights', icon: <Plane size={20} />, label: 'Мои полёты', tour: 'my-flights' },
    { key: '/pilot-workspace', icon: <Radar size={20} />, label: 'Моя статистика', tour: 'pilot-workspace' },
  ];
}

function getViewerNav(): NavItem[] {
  return [
    { key: '/dashboard', icon: <LayoutDashboard size={20} />, label: 'Главная' },
    { key: '/analytics', icon: <BarChart3 size={20} />, label: 'Аналитика' },
  ];
}

/** Returns the navigation items for a specific user role */
function getNavForUser(
  role: UserNavRole,
  counts?: { pendingFlights?: number; openEvents?: number },
  services?: Array<{ code: string; name: string }>,
): NavItem[] {
  let items: NavItem[];
  switch (role) {
    case 'admin':
      items = getAdminNav(services); break;
    case 'grpi':
      items = getGrpiNav(counts?.pendingFlights); break;
    case 'service':
      items = getServiceNav(counts?.openEvents, services); break;
    case 'pilot':
      items = getPilotNav(); break;
    default:
      items = getViewerNav();
  }
  // "Помощь" доступна всем ролям
  items.push(
    { key: '_divider_help', label: '', divider: true },
    { key: '/help', icon: <HelpCircle size={20} />, label: 'Помощь' },
  );
  return items;
}

/** Returns the default landing page for a user role */
export function getDefaultRoute(user: User | null): string {
  const role = getUserNavRole(user);
  switch (role) {
    case 'admin':
      return '/dashboard';
    case 'grpi':
      return '/dashboard';
    case 'service': {
      const svc = user?.services?.[0];
      return svc ? `/service/${svc.code}` : '/safety/events';
    }
    case 'pilot':
      return '/pilot-workspace';
    default:
      return '/dashboard';
  }
}

/** Human-readable role label */
function getRoleLabel(role: UserNavRole): string {
  switch (role) {
    case 'admin':
      return 'Администратор';
    case 'grpi':
      return 'Аналитик ГРПИ';
    case 'service':
      return 'Сотрудник службы';
    case 'pilot':
      return 'Пилот';
    default:
      return 'Наблюдатель';
  }
}

// ============================================================
// Component
// ============================================================

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout, isImpersonating, stopImpersonation } = useAuthStore();
  const { theme: currentTheme, sidebarCollapsed, toggleTheme, toggleSidebar } = useUIStore();
  const brand = getBrand();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const { startTour, shouldShowTour } = useOnboardingTour();

  useEffect(() => {
    if (shouldShowTour()) {
      const timer = setTimeout(() => startTour(), 1500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleStopImpersonation = () => {
    // Восстанавливаем исходного админа из localStorage (не нужен API-вызов)
    stopImpersonation();
    navigate('/dashboard');
  };

  const userRole = useMemo(() => getUserNavRole(user), [user]);
  const serviceName = useMemo(() => getUserServiceName(user), [user]);

  // Fetch system settings (organization name/logo)
  const { data: systemSettings } = useQuery({
    queryKey: ['system-settings'],
    queryFn: getSystemSettings,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch unread notification count
  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ['unreadNotificationCount'],
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const unreadCount = unreadData?.count ?? 0;

  // Fetch pending flights count (for GRPI)
  const { data: flightsData } = useQuery<{ total: number }>({
    queryKey: ['navFlightsCount'],
    queryFn: () => getFlights({ per_page: 1, status: 'completed' }),
    enabled: userRole === 'grpi',
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const pendingFlightsCount = flightsData?.total;

  // Fetch open events count (for service users)
  const { data: eventsData } = useQuery<{ total: number }>({
    queryKey: ['navEventsCount'],
    queryFn: () => getSafetyEvents({ per_page: 1, status: 'submitted,under_review,investigation' }),
    enabled: userRole === 'service',
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const openEventsCount = eventsData?.total;

  // Load all services for admin menu (shows each service as nav item)
  const { data: allServicesData } = useQuery<{ items: Array<{ id: number; code: string; name: string; description?: string; is_flight_data_service?: boolean }> }>({
    queryKey: ['allServicesNav'],
    queryFn: () => getServices({ per_page: 50, is_active: true }),
    enabled: userRole === 'admin',
    staleTime: 5 * 60 * 1000,
  });
  const navServices = userRole === 'admin'
    ? (allServicesData?.items?.filter((s) => !s.is_flight_data_service) || [])
    : user?.services;

  // Build navigation
  const navItems = useMemo(
    () =>
      getNavForUser(
        userRole,
        { pendingFlights: pendingFlightsCount, openEvents: openEventsCount },
        navServices,
      ),
    [userRole, pendingFlightsCount, openEventsCount, navServices],
  );

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navItems.forEach((item) => {
      if (item.children) {
        const isActive = item.children.some((c) => location.pathname.startsWith(c.key));
        if (isActive) initial[item.key] = true;
      }
    });
    return initial;
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const pageTitle = useMemo(() => {
    const map: Record<string, string> = {
      '/dashboard': 'Центр управления',
      '/flights': 'Полёты',
      '/safety/events': 'События безопасности',
      '/safety/risk-map': 'Карта рисков',
      '/safety/catalog': 'Каталог событий',
      '/analytics': 'Аналитика',
      '/reports': 'Отчёты',
      '/reginfo-analysis': 'Анализ параметров',
      '/accounting-journal': 'Журнал смены',
      '/sync': 'Синхронизация MDB',
      '/system-status': 'Состояние системы',
      '/admin/users': 'Пользователи',
      '/admin/roles': 'Роли и права',
      '/admin/services': 'Службы',
      '/admin/settings': 'Настройки',
      '/pilots': 'Лётный состав',
      '/pilot-workspace': 'Статистика пилота',
      '/fleet': 'Парк ВС',
      '/airports': 'Аэропорты',
      '/organization': 'Эскадрильи',
      '/audit-log': 'Журнал аудита',
      '/auth-journal': 'Журнал авторизаций',
      '/notifications': 'Уведомления',
      '/profile': 'Профиль',
      '/help': 'Помощь',
      '/search': 'Поиск',
    };
    const p = location.pathname;
    if (map[p]) return map[p];
    if (p.startsWith('/flights/')) return 'Карточка полёта';
    if (p.startsWith('/safety/events/')) return 'Событие безопасности';
    if (p.startsWith('/pilots/')) return 'Пилот';
    if (p.startsWith('/airports/')) return 'Аэропорт';
    if (p.startsWith('/pilot-workspace/')) return 'Статистика пилота';
    if (p.startsWith('/service/')) return 'Служба';
    return '';
  }, [location.pathname]);

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleUserMenuClose = () => setAnchorEl(null);

  // On mobile the sidebar is always shown expanded (never collapsed)
  const isCollapsed = isMobile ? false : sidebarCollapsed;
  const drawerWidth = isMobile ? DRAWER_WIDTH : (sidebarCollapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH);

  /** Navigate and close drawer on mobile */
  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileDrawerOpen(false);
  };

  // Collapsed sidebar: popover submenu state
  const [subMenuAnchor, setSubMenuAnchor] = useState<HTMLElement | null>(null);
  const [subMenuKey, setSubMenuKey] = useState<string | null>(null);
  const subMenuCloseTimer = useRef<ReturnType<typeof setTimeout>>();

  const handleSubMenuOpen = (e: React.MouseEvent<HTMLElement>, key: string) => {
    clearTimeout(subMenuCloseTimer.current);
    setSubMenuAnchor(e.currentTarget);
    setSubMenuKey(key);
  };
  const handleSubMenuClose = () => {
    subMenuCloseTimer.current = setTimeout(() => {
      setSubMenuAnchor(null);
      setSubMenuKey(null);
    }, 200);
  };
  const handleSubMenuEnter = () => {
    clearTimeout(subMenuCloseTimer.current);
  };

  // Active item styling (brand-aware)
  const activeItemSx = {
    bgcolor: brand.sidebarActiveBg[theme.palette.mode],
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      bgcolor: brand.sidebarActiveBg[theme.palette.mode],
      filter: 'brightness(1.1)',
    },
  };

  const inactiveItemSx = {
    bgcolor: 'transparent',
    color: theme.palette.text.secondary,
    '&:hover': {
      bgcolor: theme.palette.action.hover,
    },
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Боковая панель */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileDrawerOpen : true}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            transition: 'width 0.2s',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Логотип + служба */}
        <Box
          sx={{
            minHeight: 64,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: isCollapsed ? 0 : 2.5,
            alignItems: isCollapsed ? 'center' : 'flex-start',
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {brand.logoIconUrl && isCollapsed ? (
              <img src={brand.logoIconUrl} alt={brand.shortName} style={{ width: 32, height: 32 }} />
            ) : (brand.logoUrl || brand.logoDarkUrl) && !isCollapsed ? (
              <img
                src={theme.palette.mode === 'dark' && brand.logoDarkUrl ? brand.logoDarkUrl : brand.logoUrl || ''}
                alt={brand.shortName}
                style={{ height: 36, maxWidth: 200 }}
              />
            ) : (
              <>
                {brand.logoIconUrl ? (
                  <img src={brand.logoIconUrl} alt="" style={{ width: 28, height: 28 }} />
                ) : (
                  <Shield size={28} color={brand.logoColor} />
                )}
                {!isCollapsed && (
                  <Typography variant="h6" sx={{ ml: 1.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {systemSettings?.organization_name || brand.shortName}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {/* Show service name for service users */}
          {!isCollapsed && serviceName && (userRole === 'grpi' || userRole === 'service') && (
            <Chip
              label={serviceName}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                mt: 0.5,
                ml: 0.25,
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            />
          )}
        </Box>

        {/* Навигация */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', mt: 1 }}>
          <List component="nav" disablePadding>
            {navItems.map((item) => {
              if (item.divider) {
                return <Divider key={item.key} sx={{ my: 1 }} />;
              }

              if (item.children) {
                const isOpen = openGroups[item.key] ?? false;
                const isChildActive = item.children.some((c) =>
                  location.pathname.startsWith(c.key),
                );

                return (
                  <Box key={item.key}>
                    <ListItemButton
                      onClick={isCollapsed ? (e: React.MouseEvent<HTMLElement>) => handleSubMenuOpen(e, item.key) : () => toggleGroup(item.key)}
                      onMouseEnter={isCollapsed ? (e) => handleSubMenuOpen(e, item.key) : undefined}
                      onMouseLeave={isCollapsed ? handleSubMenuClose : undefined}
                      sx={{
                        minHeight: 44,
                        justifyContent: isCollapsed ? 'center' : 'initial',
                        px: 2.5,
                        borderRadius: 1,
                        mx: 1,
                        mb: 0.5,
                        color: isChildActive
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      }}
                    >
                      {item.icon && (
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: isCollapsed ? 0 : 2,
                            justifyContent: 'center',
                            color: 'inherit',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                      )}
                      {!isCollapsed && (
                        <>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ fontSize: 14, fontWeight: isChildActive ? 600 : 400 }}
                          />
                          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </>
                      )}
                    </ListItemButton>
                    {!isCollapsed && (
                      <Collapse in={isOpen} timeout={200}>
                        <List disablePadding>
                          {item.children.map((child) => {
                            const isActive = location.pathname === child.key ||
                              location.pathname.startsWith(child.key + '/');
                            return (
                              <ListItemButton
                                key={child.key}
                                onClick={() => handleNavClick(child.key)}
                                {...(child.tour ? { 'data-tour': child.tour } : {})}
                                sx={{
                                  pl: 5.5,
                                  minHeight: 38,
                                  borderRadius: 1,
                                  mx: 1,
                                  mb: 0.25,
                                  ...(isActive ? activeItemSx : inactiveItemSx),
                                }}
                              >
                                {child.icon && (
                                  <ListItemIcon
                                    sx={{ minWidth: 0, mr: 1.5, color: 'inherit' }}
                                  >
                                    {child.icon}
                                  </ListItemIcon>
                                )}
                                <ListItemText
                                  primary={child.label}
                                  secondary={child.description || undefined}
                                  primaryTypographyProps={{
                                    fontSize: 13,
                                    fontWeight: isActive ? 600 : 400,
                                    lineHeight: child.description ? 1.3 : undefined,
                                  }}
                                  secondaryTypographyProps={child.description ? {
                                    fontSize: 11,
                                    noWrap: true,
                                    sx: { opacity: 0.55, lineHeight: 1.2 },
                                  } : undefined}
                                />
                                {child.badge != null && child.badge > 0 && (
                                  <Chip
                                    label={child.badge > 99 ? '99+' : child.badge}
                                    size="small"
                                    color="primary"
                                    sx={{
                                      height: 20,
                                      minWidth: 20,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                );
              }

              const isActive =
                location.pathname === item.key ||
                location.pathname.startsWith(item.key + '/');

              return (
                <ListItemButton
                  key={item.key}
                  onClick={() => handleNavClick(item.key)}
                  {...(item.tour ? { 'data-tour': item.tour } : {})}
                  sx={{
                    minHeight: 44,
                    justifyContent: isCollapsed ? 'center' : 'initial',
                    px: 2.5,
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    ...(isActive ? activeItemSx : inactiveItemSx),
                  }}
                >
                  {item.icon && (
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isCollapsed ? 0 : 2,
                        justifyContent: 'center',
                        color: 'inherit',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  )}
                  {!isCollapsed && (
                    <>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 400 }}
                      />
                      {item.badge != null && item.badge > 0 && (
                        <Chip
                          label={item.badge > 99 ? '99+' : item.badge}
                          size="small"
                          color="primary"
                          sx={{
                            height: 20,
                            minWidth: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                    </>
                  )}
                </ListItemButton>
              );
            })}
          </List>

          {/* Collapsed sidebar: popover submenu */}
          {isCollapsed && (
            <Popover
              open={Boolean(subMenuAnchor)}
              anchorEl={subMenuAnchor}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              onClose={() => { setSubMenuAnchor(null); setSubMenuKey(null); }}
              disableRestoreFocus
              sx={{ pointerEvents: 'none' }}
              slotProps={{
                paper: {
                  sx: { pointerEvents: 'auto', minWidth: 220, py: 0.5, borderRadius: 2 },
                  onMouseEnter: handleSubMenuEnter,
                  onMouseLeave: handleSubMenuClose,
                },
              }}
            >
              {(() => {
                const group = navItems.find((n) => n.key === subMenuKey && n.children);
                if (!group || !group.children) return null;
                return (
                  <List dense disablePadding>
                    <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600, fontSize: 13, bgcolor: 'transparent' }}>
                      {group.label}
                    </ListSubheader>
                    {group.children.map((child) => {
                      const isActive = location.pathname === child.key || location.pathname.startsWith(child.key + '/');
                      return (
                        <ListItemButton
                          key={child.key}
                          onClick={() => { handleNavClick(child.key); setSubMenuAnchor(null); setSubMenuKey(null); }}
                          sx={{ ...(isActive ? activeItemSx : inactiveItemSx), borderRadius: 1, mx: 0.5, minHeight: 36 }}
                        >
                          {child.icon && <ListItemIcon sx={{ minWidth: 0, mr: 1.5, color: 'inherit' }}>{child.icon}</ListItemIcon>}
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: 13, fontWeight: isActive ? 600 : 400 }} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                );
              })()}
            </Popover>
          )}
        </Box>

        {/* Sidebar footer: user info + collapse */}
        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            px: isCollapsed ? 0.5 : 2,
            py: 1.5,
          }}
        >
          {!isCollapsed && (
            <Box sx={{ mb: 1, px: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.full_name || user?.username || 'Пользователь'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11 }}
              >
                {getRoleLabel(userRole)}
              </Typography>
            </Box>
          )}
          {/* Hide collapse toggle on mobile */}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton onClick={toggleSidebar} size="small" sx={{ color: theme.palette.text.secondary }}>
                {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </IconButton>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Основная область */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Шапка */}
        <AppBar
          position="sticky"
          color="default"
          elevation={0}
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Toolbar sx={{ justifyContent: 'flex-end', gap: 1 }}>
            {/* Hamburger menu on mobile */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ mr: 0, color: theme.palette.text.secondary }}
              >
                <MenuIcon size={20} />
              </IconButton>
            )}

            {/* Текущий раздел */}
            {pageTitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mr: 'auto',
                  fontWeight: 500,
                  display: { xs: 'none', md: 'block' },
                  ml: isMobile ? 0 : 1,
                }}
              >
                {pageTitle}
              </Typography>
            )}
            {!pageTitle && <Box sx={{ mr: 'auto' }} />}

            {/* Глобальный поиск */}
            <GlobalSearch />

            {/* Переключатель темы */}
            <Tooltip title={currentTheme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
              <IconButton
                onClick={toggleTheme}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  transition: 'transform 0.3s ease, color 0.3s ease',
                  '&:hover': {
                    transform: 'rotate(20deg)',
                    color: theme.palette.text.primary,
                  },
                }}
              >
                {currentTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </IconButton>
            </Tooltip>

            {/* Уведомления */}
            <Tooltip title="Уведомления">
              <IconButton
                onClick={() => navigate('/notifications')}
                sx={{ color: theme.palette.text.secondary }}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <Bell size={20} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Меню пользователя */}
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                ml: 1,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                '&:hover': { bgcolor: theme.palette.action.hover },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.primary.main,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {user?.full_name?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 500, display: { xs: 'none', sm: 'block' } }}>
                {user?.full_name || user?.username || 'Пользователь'}
              </Typography>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate('/profile');
                }}
              >
                Профиль
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate('/admin/settings');
                }}
              >
                Настройки
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  logout();
                  navigate('/login');
                }}
                sx={{ color: theme.palette.error.main }}
              >
                <LogOut size={16} style={{ marginRight: 8 }} />
                Выход
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Контент */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            minHeight: 0,
          }}
        >
          {isImpersonating && (
            <Box
              sx={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
                color: '#fff',
                px: { xs: 1.5, md: 3 },
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                borderRadius: 2,
                mb: 2,
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { boxShadow: '0 4px 20px rgba(245, 158, 11, 0.4)' },
                  '50%': { boxShadow: '0 4px 30px rgba(239, 68, 68, 0.6)' },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', bgcolor: '#fff',
                  animation: 'blink 1s ease-in-out infinite',
                  '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                }} />
                <Typography variant="body2" fontWeight={700} sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)', display: { xs: 'none', sm: 'block' } }}>
                  РЕЖИМ ИМПЕРСОНАЦИИ
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: 12, sm: 14 } }}>
                  Вы просматриваете как: <strong>{user?.full_name}</strong>
                  {user?.services && user.services.length > 0 && (
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}> ({user.services.map(s => s.name).join(', ')})</Box>
                  )}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleStopImpersonation}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontWeight: 700,
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                  whiteSpace: 'nowrap',
                  fontSize: { xs: 12, sm: 14 },
                }}
              >
                Вернуться
              </Button>
            </Box>
          )}
          <PageTransition key={location.pathname}>
            <Outlet />
          </PageTransition>
        </Box>

        {/* Футер */}
        <Box
          component="footer"
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            px: { xs: 1.5, md: 3 },
            py: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {(brand.logoUrl || brand.logoDarkUrl) && (
            <img
              src={theme.palette.mode === 'dark' && brand.logoDarkUrl ? brand.logoDarkUrl : brand.logoUrl || ''}
              alt={brand.shortName}
              style={{ height: 18, opacity: 0.5 }}
            />
          )}
          <Typography variant="caption" color="text.disabled">
            &copy; {new Date().getFullYear()} {brand.shortName} &mdash; {brand.footerText}
          </Typography>
        </Box>
      </Box>
      <CommandPalette />
      <AIAssistant />
    </Box>
  );
};

export default AppLayout;
